import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface TrackInfo {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  description: string;
  features: string[];
  is_active: boolean;
}

interface TrackPricing {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  description: string;
  duration_hours: number;
  use_traction_compound: boolean;
  is_active: boolean;
}

export default function TrackInfo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTrackType, setSelectedTrackType] = useState<'drag' | 'drift'>('drag');
  const [trackInfo, setTrackInfo] = useState<TrackInfo[]>([]);
  const [pricing, setPricing] = useState<TrackPricing[]>([]);

  useEffect(() => {
    fetchTrackInfo();
    fetchPricing();
  }, [selectedTrackType]);

  const fetchTrackInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('track_info')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;
      setTrackInfo(data || []);
    } catch (error) {
      console.error('Error fetching track info:', error);
    }
  };

  const fetchPricing = async () => {
    try {
      const { data, error } = await supabase
        .from('track_pricing')
        .select('*')
        .eq('track_type', selectedTrackType)
        .eq('is_active', true)
        .order('duration_hours', { ascending: true });

      if (error) throw error;
      setPricing(data || []);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleBookNow = () => {
    if (!user) {
      navigate('/login', { state: { from: '/services' } });
    } else {
      navigate('/services');
    }
  };

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-white text-center mb-12">Our Tracks</h1>

        {/* Track Type Selection */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => setSelectedTrackType('drag')}
            className={`px-6 py-3 rounded-lg text-lg font-semibold transition-colors ${
              selectedTrackType === 'drag'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Drag Strip
          </button>
          <button
            onClick={() => setSelectedTrackType('drift')}
            className={`px-6 py-3 rounded-lg text-lg font-semibold transition-colors ${
              selectedTrackType === 'drift'
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            Drift Track
          </button>
        </div>

        {/* Track Information */}
        {trackInfo
          .filter(track => track.track_type === selectedTrackType)
          .map(track => (
            <div key={track.id}>
              <div className="bg-zinc-900 rounded-lg p-8 mb-8">
                <h2 className="text-3xl font-bold text-white mb-4">{track.title}</h2>
                <p className="text-xl text-gray-400 mb-8">{track.description}</p>
                
                <h3 className="text-xl font-semibold text-white mb-4">Track Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {track.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary-500 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Options */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-6">Service Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pricing.map((option) => (
                    <div key={option.id} className="bg-zinc-900 rounded-lg p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="h-5 w-5 text-primary-500" />
                        <h4 className="text-xl font-semibold text-white">{option.title}</h4>
                      </div>
                      <p className="text-gray-400 mb-4">{option.description}</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {option.duration_hours} {option.duration_hours === 1 ? 'Hour' : 'Hours'}
                        </span>
                        {option.use_traction_compound && (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Traction Compound
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-zinc-800 rounded-lg p-8 text-center">
                {!user ? (
                  <>
                    <p className="text-xl text-gray-400 mb-6">
                      Please login or register to view pricing and make a booking.
                    </p>
                    <button
                      onClick={handleBookNow}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold"
                    >
                      Login to Book
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xl text-gray-400 mb-6">
                      Ready to book your track time?
                    </p>
                    <button
                      onClick={() => navigate('/services')}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold"
                    >
                      View Pricing & Book Now
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}